import { createWithEqualityFn } from 'zustand/traditional'
import { trackerApi } from '../api'
import type {
  TrackerItem,
  TrackerComment,
  TrackerStatusChange,
  TrackerStats,
  TrackerTag,
  TrackerStatus,
  TrackerCategory,
  TrackerPriority,
  PaginationInfo,
} from '../types'

interface TrackerFilters {
  status?: TrackerStatus | 'all'
  category?: TrackerCategory | 'all'
  priority?: TrackerPriority | 'all'
  assignedToId?: string
  search?: string
  tagId?: string
}

interface TrackerState {
  // Data
  items: TrackerItem[]
  selectedItem: TrackerItem | null
  comments: TrackerComment[]
  statusHistory: TrackerStatusChange[]
  tags: TrackerTag[]
  stats: TrackerStats | null
  pagination: PaginationInfo | null

  // Filters
  filters: TrackerFilters
  sortBy: string
  sortOrder: string

  // UI state
  isLoading: boolean
  error: string | null
  viewMode: 'list' | 'board'

  // Actions
  fetchItems: () => Promise<void>
  fetchItem: (id: string) => Promise<void>
  createItem: (data: {
    title: string
    description: string
    category?: TrackerCategory
    priority?: TrackerPriority
    assignedToId?: string
    tagIds?: string[]
    dueDate?: string
  }) => Promise<TrackerItem | null>
  updateItem: (id: string, data: Partial<TrackerItem>) => Promise<void>
  changeStatus: (id: string, status: TrackerStatus, reason?: string) => Promise<void>
  assignItem: (id: string, assignedToId: string | null) => Promise<void>
  fetchComments: (id: string) => Promise<void>
  addComment: (id: string, content: string, parentCommentId?: string) => Promise<void>
  fetchStatusHistory: (id: string) => Promise<void>
  fetchTags: () => Promise<void>
  createTag: (name: string, color?: string) => Promise<void>
  addTag: (itemId: string, tagId: string) => Promise<void>
  removeTag: (itemId: string, tagId: string) => Promise<void>
  promoteDocFeedback: (
    feedbackId: string,
    data?: { title?: string; category?: TrackerCategory; priority?: TrackerPriority }
  ) => Promise<TrackerItem | null>
  fetchStats: () => Promise<void>
  setFilters: (filters: Partial<TrackerFilters>) => void
  setPage: (page: number) => void
  setViewMode: (mode: 'list' | 'board') => void
  clearSelectedItem: () => void
}

export const useTrackerStore = createWithEqualityFn<TrackerState>((set, get) => ({
  // Initial state
  items: [],
  selectedItem: null,
  comments: [],
  statusHistory: [],
  tags: [],
  stats: null,
  pagination: null,
  filters: {},
  sortBy: 'createdAt',
  sortOrder: 'desc',
  isLoading: false,
  error: null,
  viewMode: 'list',

  // Actions
  fetchItems: async () => {
    set({ isLoading: true, error: null })
    try {
      const { filters, sortBy, sortOrder, pagination } = get()
      const res = await trackerApi.listItems({
        page: pagination?.page || 1,
        limit: 20,
        status: filters.status === 'all' ? undefined : filters.status,
        category: filters.category === 'all' ? undefined : filters.category,
        priority: filters.priority === 'all' ? undefined : filters.priority,
        assignedToId: filters.assignedToId,
        search: filters.search,
        tagId: filters.tagId,
        sortBy,
        sortOrder,
      } as Parameters<typeof trackerApi.listItems>[0])
      set({ items: res.data, pagination: res.pagination, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  fetchItem: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const res = await trackerApi.getItem(id)
      set({ selectedItem: res.data, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  createItem: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const res = await trackerApi.createItem(data)
      if (res.success) {
        set((state) => ({ items: [res.data, ...state.items], isLoading: false }))
        return res.data
      }
      set({ error: res.error || 'Failed to create item', isLoading: false })
      return null
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
      return null
    }
  },

  updateItem: async (id, data) => {
    try {
      const res = await trackerApi.updateItem(id, data)
      if (res.success) {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? res.data : i)),
          selectedItem: state.selectedItem?.id === id ? res.data : state.selectedItem,
        }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  changeStatus: async (id, status, reason) => {
    try {
      const res = await trackerApi.changeStatus(id, status, reason)
      if (res.success) {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? res.data : i)),
          selectedItem: state.selectedItem?.id === id ? res.data : state.selectedItem,
        }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  assignItem: async (id, assignedToId) => {
    try {
      const res = await trackerApi.assignItem(id, assignedToId)
      if (res.success) {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? res.data : i)),
          selectedItem: state.selectedItem?.id === id ? res.data : state.selectedItem,
        }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchComments: async (id) => {
    try {
      const res = await trackerApi.listComments(id)
      set({ comments: res.data })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  addComment: async (id, content, parentCommentId) => {
    try {
      const res = await trackerApi.addComment(id, content, parentCommentId)
      if (res.success) {
        set((state) => ({ comments: [...state.comments, res.data] }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchStatusHistory: async (id) => {
    try {
      const res = await trackerApi.getStatusHistory(id)
      set({ statusHistory: res.data })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchTags: async () => {
    try {
      const res = await trackerApi.listTags()
      set({ tags: res.data })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  createTag: async (name, color) => {
    try {
      const res = await trackerApi.createTag(name, color)
      if (res.success) {
        set((state) => ({ tags: [...state.tags, res.data] }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  addTag: async (itemId, tagId) => {
    try {
      await trackerApi.addTag(itemId, tagId)
      // Refresh item to get updated tags
      const res = await trackerApi.getItem(itemId)
      if (res.success) {
        set((state) => ({
          items: state.items.map((i) => (i.id === itemId ? res.data : i)),
          selectedItem: state.selectedItem?.id === itemId ? res.data : state.selectedItem,
        }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  removeTag: async (itemId, tagId) => {
    try {
      await trackerApi.removeTag(itemId, tagId)
      const res = await trackerApi.getItem(itemId)
      if (res.success) {
        set((state) => ({
          items: state.items.map((i) => (i.id === itemId ? res.data : i)),
          selectedItem: state.selectedItem?.id === itemId ? res.data : state.selectedItem,
        }))
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  promoteDocFeedback: async (feedbackId, data) => {
    set({ isLoading: true, error: null })
    try {
      const res = await trackerApi.promoteDocFeedback(feedbackId, data)
      if (res.success) {
        set((state) => ({ items: [res.data, ...state.items], isLoading: false }))
        return res.data
      }
      set({ error: res.error || 'Failed to promote feedback', isLoading: false })
      return null
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
      return null
    }
  },

  fetchStats: async () => {
    try {
      const res = await trackerApi.getStats()
      set({ stats: res.data })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: state.pagination ? { ...state.pagination, page: 1 } : null,
    }))
  },

  setPage: (page) => {
    set((state) => ({
      pagination: state.pagination ? { ...state.pagination, page } : null,
    }))
  },

  setViewMode: (mode) => set({ viewMode: mode }),
  clearSelectedItem: () => set({ selectedItem: null, comments: [], statusHistory: [] }),
}))

// Selectors
export const selectTrackerItems = (state: TrackerState) => state.items
export const selectTrackerStats = (state: TrackerState) => state.stats
export const selectSelectedTrackerItem = (state: TrackerState) => state.selectedItem
export const selectTrackerTags = (state: TrackerState) => state.tags
export const selectTrackerFilters = (state: TrackerState) => state.filters
export const selectTrackerIsLoading = (state: TrackerState) => state.isLoading
